/**
 * Score submission service for luckyanimal-nawabari.
 *
 * 本プロジェクトの platformId は環境によって `web_guest` / `line_mini` /
 * `android` / `ios` のいずれかになる。`web_guest` は Hub 仕様で
 * `allowWebGuestScores: true` がゲーム単位に設定されていない限り 403
 * `GUEST_NOT_ALLOWED` となる（API_Scores.md / Leaderboards 節）。
 *
 * - Guest only: SDK method -> fetch fallback pattern.
 * - Signed-in / native session: fetch only, so a stale SDK guest session cannot
 *   write the same score to the previous guest user.
 * - period は常に明示指定。
 * - API は daily / weekly 投稿時に alltime を自動更新するため、alltime は直接送らない。
 * - POST /scores は 1 req/sec per user のため、daily / weekly は直列送信する。
 *
 * エラーポリシー（fail-closed 寄り、antipatterns.md §1 準拠）:
 *   - 401（認証エラー）は notifyReauthRequired を経由して呼び出し元へ throw。
 *     送信不能を「ハイスコア反映なし」として無視せず、再ログイン UI に倒す。
 *   - 403 GUEST_NOT_ALLOWED は web_guest の仕様上正常な失敗として
 *     `false` を返す（呼び出し元はスコア送信不可と認識）。
 *   - その他 4xx / 5xx / NetworkError は warn を残しつつ false を返す
 *     （呼び出し元 ResultScreen で再試行検討材料にする）。
 */

import {
  getYmgSdk,
  getGameId,
  ymgFetch,
  isSdkAuthError,
  hasCapacitorSessionToken,
} from './sdk'
import { getAuthState } from './auth'
import { ApiError, NetworkError, notifyReauthRequired } from './ymgAuth'
import type { SessionVerification } from './gameSession'
import {
  enqueueScoreSubmission,
  processScoreQueue,
  type QueuedScorePeriod,
} from './scoreQueue'

type Period = QueuedScorePeriod
const PERIODS: QueuedScorePeriod[] = ['daily', 'weekly']
const SCORE_SUBMIT_INTERVAL_MS = 1_100
let lastQueueSubmitAt = 0

function isGuestNotAllowed(err: unknown): boolean {
  const status = err instanceof ApiError ? err.status : getErrorStatus(err)
  if (status !== 403) return false
  const body =
    err instanceof ApiError
      ? (err.body as { error?: { code?: string } } | undefined)
      : ((err as { body?: { error?: { code?: string } } } | undefined)?.body)
  return body?.error?.code === 'GUEST_NOT_ALLOWED'
}

async function submitOnePeriod(
  score: number,
  period: Period,
  preferFetch: boolean,
  sessionVerification?: SessionVerification,
): Promise<boolean> {
  const gameId = getGameId()

  if (preferFetch) {
    return submitOnePeriodWithFetch(gameId, score, period, sessionVerification)
  }

  try {
    const sdk = await getYmgSdk()
    if (typeof sdk.api?.submitScore === 'function') {
      await sdk.api.submitScore({ gameId, score, period, sessionVerification })
      return true
    }
  } catch (err) {
    if (isGuestNotAllowed(err)) {
      return false
    }
    if (isSdkAuthError(err)) {
      const error =
        err instanceof ApiError
          ? err
          : new ApiError(401, { message: 'SDK submitScore auth failed' }, undefined, true)
      notifyReauthRequired(error)
      throw error
    }
    console.warn(
      `[Scores] sdk.api.submitScore(${period}) failed (non-auth), trying fetch:`,
      err,
    )
  }

  return submitOnePeriodWithFetch(gameId, score, period, sessionVerification)
}

async function submitOnePeriodWithFetch(
  gameId: string,
  score: number,
  period: Period,
  sessionVerification?: SessionVerification,
): Promise<boolean> {
  try {
    await ymgFetch(`/games/${gameId}/scores`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ score, period, sessionVerification }),
    })
    return true
  } catch (err) {
    // 401 は ymgFetch 内で notifyReauthRequired 済み。throw で伝播させて
    // 呼び出し元が再認証 UI に倒せるようにする（サイレントフォールバック禁止）。
    if (err instanceof ApiError && err.requiresReauth) {
      throw err
    }
    // 403 GUEST_NOT_ALLOWED は web_guest の仕様上の失敗として吸収。
    if (isGuestNotAllowed(err)) {
      return false
    }
    if (err instanceof ApiError || err instanceof NetworkError) {
      throw err
    }
    // 想定外のエラーは握り潰さず throw（バグの早期発見）。
    throw err
  }
}

async function shouldPreferFetchForScores(): Promise<boolean> {
  if (getAuthState().status === 'signed_in') return true
  return hasCapacitorSessionToken()
}

/**
 * Submit a game score to YMG Hub for daily / weekly.
 * Returns true if at least one period succeeded.
 *
 * 401 は `submitOnePeriod` 内で throw されるため、呼び出し元に伝播する
 * （再認証導線への接続）。
 */
export async function submitGameScore(score: number): Promise<boolean> {
  const safeScore = Math.max(0, Math.round(score))
  return submitGameScoreWithOptions({ score: safeScore })
}

export async function submitVerifiedGameScore(params: {
  score: number
  sessionVerification?: SessionVerification | null
}): Promise<boolean> {
  const safeScore = Math.max(0, Math.round(params.score))
  const verification =
    params.sessionVerification
      ? { ...params.sessionVerification, finalScore: safeScore }
      : undefined
  return submitGameScoreWithOptions({ score: safeScore, sessionVerification: verification })
}

async function submitGameScoreWithOptions(params: {
  score: number
  sessionVerification?: SessionVerification
}): Promise<boolean> {
  const safeScore = params.score
  const preferFetch = await shouldPreferFetchForScores()
  let ok = false
  let reauthError: ApiError | null = null

  for (const [i, period] of PERIODS.entries()) {
    if (i > 0) {
      await new Promise((resolve) => setTimeout(resolve, SCORE_SUBMIT_INTERVAL_MS))
    }
    try {
      const periodOk = await submitOnePeriod(
        safeScore,
        period,
        preferFetch,
        params.sessionVerification,
      )
      ok = ok || periodOk
    } catch (err) {
      if (err instanceof ApiError && err.requiresReauth) {
        enqueueFailedScore(safeScore, period, params.sessionVerification)
        reauthError = err
        continue
      }
      if (isRetryableScoreError(err)) {
        enqueueFailedScore(safeScore, period, params.sessionVerification)
        continue
      }
      if (isPermanentScoreError(err)) {
        console.warn(`[Scores] permanent submit failure for ${period}:`, err)
        continue
      }
      throw err
    }
  }

  if (reauthError) {
    throw reauthError
  }

  if (ok) {
    console.log('[Scores] Submitted:', safeScore)
  } else {
    console.warn('[Scores] All periods failed or queued for score:', safeScore)
  }
  return ok
}

export async function drainQueuedScores(): Promise<number> {
  return processScoreQueue(async (payload) => {
    await waitForQueueRateLimit()
    const preferFetch = await shouldPreferFetchForScores()
    try {
      const success = await submitOnePeriod(
        payload.score,
        payload.period,
        preferFetch,
        payload.sessionVerification,
      )
      lastQueueSubmitAt = Date.now()
      return { success, permanent: !success }
    } catch (err) {
      if (err instanceof ApiError && err.requiresReauth) {
        return { success: false }
      }
      if (isRetryableScoreError(err)) {
        return { success: false }
      }
      if (isPermanentScoreError(err)) {
        return { success: false, permanent: true }
      }
      throw err
    }
  })
}

function enqueueFailedScore(
  score: number,
  period: QueuedScorePeriod,
  sessionVerification?: SessionVerification,
): void {
  const queued = enqueueScoreSubmission({ score, period, sessionVerification })
  if (queued) {
    console.warn(`[Scores] queued ${period} score for retry:`, score)
  }
}

function isRetryableScoreError(err: unknown): boolean {
  if (err instanceof NetworkError) return true
  if (!(err instanceof ApiError)) return false
  return err.status === 429 || err.status >= 500
}

function isPermanentScoreError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false
  if (err.requiresReauth) return false
  if (err.status === 429 || err.status >= 500) return false
  return err.status >= 400
}

function getErrorStatus(err: unknown): number | undefined {
  if (!err || typeof err !== 'object') return undefined
  const status = (err as { status?: unknown }).status
  return typeof status === 'number' ? status : undefined
}

async function waitForQueueRateLimit(): Promise<void> {
  const elapsed = Date.now() - lastQueueSubmitAt
  if (elapsed >= SCORE_SUBMIT_INTERVAL_MS) return
  await new Promise((resolve) => setTimeout(resolve, SCORE_SUBMIT_INTERVAL_MS - elapsed))
}
